import {
  boxShadow,
  BoxShadowProps,
  color,
  ColorProps,
  compose,
  css,
  flexbox,
  FlexboxProps,
  layout,
  LayoutProps,
  ResponsiveStyleValue,
  space,
  SpaceProps,
  variant,
} from '@wrisk/styled-system'
import React from 'react'
import styled from 'styled-components'

import { withDefaultProps } from '../../defaultProps'
import { transition, TransitionProps } from '../../transition'
import { gap, GapProps } from '../containers/gap'
import { Icon } from '../images'
import {
  textTransform,
  TextTransformProps,
  Typo,
  whiteSpace,
  WhiteSpaceProps,
} from '../typography'
import { Button, ButtonProps } from './Button'
import {
  buttonDisabledVariants,
  ButtonLayoutProps,
  buttonLoadingVariants,
  buttonStateMapping,
  ButtonStateProps,
  defaultButtonStyles,
} from './variants'

export type SSOButtonProperty = 'default' | 'inverted'

export interface SSOButtonProps
  extends ButtonProps,
    BoxShadowProps,
    ButtonLayoutProps,
    ColorProps,
    FlexboxProps,
    GapProps,
    LayoutProps,
    SpaceProps,
    TextTransformProps,
    TransitionProps,
    WhiteSpaceProps {
  variant?: ResponsiveStyleValue<SSOButtonProperty>
}

const defaultProps = withDefaultProps<SSOButtonProps>({
  variant: 'default',
  layout: 'default',
})

const StyledButton = styled(Button)
  .attrs<ButtonStateProps>(buttonStateMapping)
  .attrs<SSOButtonProps>(defaultProps)(
  defaultButtonStyles,
  css({
    px: 6,
    py: 5,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    textTransform: 'inherit',
    '&:hover:not([disabled])': {
      boxShadow: 'inset 0 0 0 100rem rgba(255,255,255,0.1)',
    },
    '&:active:not([disabled])': {
      boxShadow: 'inset 0 0 0 100rem rgba(255,255,255,0.2)',
    },
    '&:focus-visible:not([disabled])': {
      outlineOffset: '2px',
      outlineStyle: 'solid',
      outlineWidth: '2px',
    },
  }),
  variant({
    scale: 'SSOButton.variants',
    variants: {
      default: {
        bg: 'body',
        color: 'chalk',
        '&:focus-visible:not([disabled])': {
          outlineColor: 'body',
        },
      },
      inverted: {
        bg: 'chalk',
        color: 'body',
        '&:focus-visible:not([disabled])': {
          outlineColor: 'chalk',
        },
      },
    },
  }),
  variant({
    prop: '$loadingState',
    scale: 'SSOButton.loading',
    variants: buttonLoadingVariants,
  }),
  variant({
    prop: '$disabledState',
    scale: 'SSOButton.disabled',
    variants: buttonDisabledVariants,
  }),
  compose(
    boxShadow,
    color,
    flexbox,
    gap,
    layout,
    space,
    textTransform,
    transition,
    whiteSpace,
  ),
)

export const SSOButton = ({ children, ...props }: SSOButtonProps) => (
  <StyledButton {...props}>
    <Icon
      color={props.variant === 'inverted' ? 'body' : 'chalk'}
      icon='brand'
      size='medium'
    />
    <Typo variant='buttons'>{children}</Typo>
    <Icon
      color={props.variant === 'inverted' ? 'body' : 'chalk'}
      icon='chevronRight'
      ml='auto'
      size='tiny'
    />
  </StyledButton>
)
